import * as React from 'react';

import { captureException } from '@sentry/browser';
import {
  ActivityPacing,
  ActivityStage,
} from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { useDispatch } from 'react-redux';

import { Stack } from '~shared/components/Stack';
import {
  TeacherApplicationContext,
  useTeacherApplicationContext,
} from '~shared/context/TeacherApplicationContext';
import { ActivityLiveEvent } from '~teacher/components/ActivityResult/ActivityLiveEvent';
import {
  ActivityResultQuestionProvider,
  useResultQuestionState,
} from '~teacher/components/ActivityResult/ActivityResultQuestionProvider';
import { InviteStudentDrawerContext } from '~teacher/components/ActivityResult/modals/InviteStudentDrawer';
import { StartVoteModal } from '~teacher/components/ActivityResult/modals/StartVoteModal';
import { ActivityResultError } from '~teacher/components/ActivityResult/States/ActivityResultError';
import { ActivityResultLoading } from '~teacher/components/ActivityResult/States/ActivityResultLoading';
import { ErrorDialog } from '~teacher/components/ErrorDialog';
import {
  QuizResultsHeaderFinished,
  QuizResultsHeaderInProgress,
} from '~teacher/components/QuizResultsHeader';
import { QuizResultsHeaderSansControls } from '~teacher/components/QuizResultsHeader/QuizResultsHeaderSansControls';
import { ReportAnalyticsProvider } from '~teacher/components/QuizResultsHeader/ReportAnalyticsContext';
import {
  ResultsQuestionAnswers,
  ResultsQuestionExplanation,
  ResultsQuestionMedia,
  ResultsQuestionNavigation,
  ResultsQuestionOptions,
  ResultsQuestionText,
} from '~teacher/components/ResultsQuestion';
import { ResultsQuestionGradingWeight } from '~teacher/components/ResultsQuestion/ResultsQuestionGradingWeight';
import { ResultsQuickLauncher } from '~teacher/components/ResultsQuickLauncher/ResultsQuickLauncher';
import { useSocrativeRouter } from '~teacher/context/RouterContext';
import {
  useActivityByInstanceId,
  useIsQuickQuestion,
  useQuiz,
  useUpdateActivityState,
} from '~teacher/hook';
import { currentRoomActivityChanged } from '~teacher/slices/teacher/teacherSlice';

import { personalQuizUrl as quizUrl } from '../../../src/api/urls';
import { legacyStartVote } from '../../api/activities/activityApi';

const QuestionContents = ({ children }: { children: React.ReactNode }) => {
  const {
    question = {
      questionText: undefined,
      questionImage: undefined,
      questionVideo: undefined,
    },
  } = useResultQuestionState();
  const hasQuestionContent =
    !!question.questionText ||
    !!question.questionImage ||
    !!question.questionVideo;
  return (
    <div className={hasQuestionContent ? 'lg:row-start-2' : 'col-span-full'}>
      {children}
    </div>
  );
};

interface ActivityQuestionPageProps {
  activityInstanceId: number;
  questionPosition: number;
}

export function ActivityQuestionPage({
  activityInstanceId,
  questionPosition,
}: ActivityQuestionPageProps) {
  const dispatch = useDispatch();

  const teacherApplicationContext = useTeacherApplicationContext();

  const socrativeRouter = useSocrativeRouter();

  // HACK Reset classes/styles added to body by Activity Results Table
  document.body.removeAttribute('style');
  document.body.removeAttribute('class');

  const activityResult = useActivityByInstanceId({
    activityInstanceId,
    pubnub: window.pubnub,
  });
  const { mutateAsync: updateActivityState } = useUpdateActivityState();

  const activity = activityResult.data?.activity;
  const quizResult = useQuiz(activity?.quizId, quizUrl);

  const isQuickQuestion = useIsQuickQuestion(activity?.activityType);
  const isFinishedActivity = activity?.activityStage === ActivityStage.Finished;

  const [isStartVoteModalOpen, setIsStartVoteModalOpen] = React.useState(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);

  const [showResults, setShowResults] = React.useState(false);
  const [showAnswers, setShowAnswers] = React.useState(isFinishedActivity);
  const [showNames, setShowNames] = React.useState(false);

  if (quizResult.isError) {
    return <ActivityResultError />;
  }

  if (activityResult.isError) {
    return <ActivityResultError />;
  }

  if (activityResult.isLoading || quizResult.isLoading) {
    return <ActivityResultLoading />;
  }

  const resetAllToggles = () => {
    setShowAnswers(isFinishedActivity);
    setShowNames(false);
    setShowResults(false);
  };

  const openErrorDialog = () => {
    setIsErrorDialogOpen(true);
  };

  const handleFinishActivity = () => {
    socrativeRouter.trigger('route'); /* @Hack to trigger route change */
    socrativeRouter.routeToResults(activityInstanceId);
  };

  if (activityResult && quizResult) {
    const { responses, students, room, questionScores } = activityResult.data;
    const { quiz, quizAnswer, quizQuestion } = quizResult.data;

    const showGradingWeight = quiz.questions.some(
      (questionId) => quizQuestion[questionId]?.gradingWeight !== 1
    );

    const isTeacherPacedActivity =
      ActivityStage.InProgress === activity.activityStage &&
      ActivityPacing.Teacher === activity?.settings?.pacing;
    // TODO These click handlers will change based on activity type / status
    const onBackToResultsClick = () => {
      socrativeRouter.routeToResults(activityInstanceId);
    };

    const handleUpdateQuestionPosition = (
      activityInstanceId,
      questionPosition: number
    ) => {
      setIsUpdating(true);
      updateActivityState({
        activityInstanceId: activityInstanceId,
        questionPosition: questionPosition,
      })
        .then((results) => {
          const { data } = results;
          const { activityState } = data;

          setIsUpdating(false);
          socrativeRouter.routeToResultsQuestion(
            activityInstanceId,
            activityState + 1,
            true
          );
        })
        .catch((error) => {
          setIsUpdating(false);
          openErrorDialog();
          captureException(error);
        });
    };

    const onPreviousQuestionClick = (questionPosition: number) => {
      resetAllToggles();
      if (isTeacherPacedActivity) {
        handleUpdateQuestionPosition(activityInstanceId, questionPosition);
      } else {
        socrativeRouter.routeToResultsQuestion(
          activityInstanceId,
          questionPosition
        );
      }
    };

    const onNextQuestionClick = (questionPosition: number) => {
      resetAllToggles();
      if (isTeacherPacedActivity) {
        handleUpdateQuestionPosition(activityInstanceId, questionPosition);
      } else {
        socrativeRouter.routeToResultsQuestion(
          activityInstanceId,
          questionPosition
        );
      }
    };

    const handleStartVote = () => {
      setIsUpdating(true);
      legacyStartVote(room)
        .then((results) => {
          setIsStartVoteModalOpen(false);
          setIsUpdating(false);
          dispatch(
            currentRoomActivityChanged({
              activityInstanceId: results.id,
              isLive: true,
            })
          );
          socrativeRouter.routeToResultsQuestion(
            // eslint-disable-next-line
            // @ts-ignore
            results.id,
            1
          );
        })
        .catch((error) => {
          console.error(error);

          captureException(error);
          setIsUpdating(false);
          setIsStartVoteModalOpen(false);
          openErrorDialog();
        });
    };

    const shouldShowNavigation =
      !isQuickQuestion ||
      (isQuickQuestion && activity.activityStage === ActivityStage.Finished);

    const title = isQuickQuestion ? translate('Quick Question') : quiz.name;

    return (
      <div>
        <ErrorDialog
          open={isErrorDialogOpen}
          onClose={() => {
            setIsErrorDialogOpen(false);
          }}
        />

        <StartVoteModal
          open={isStartVoteModalOpen}
          onClose={() => {
            setIsStartVoteModalOpen(false);
          }}
          isSendVoteDisabled={isUpdating}
          onStartVoteClick={handleStartVote}
          showStartVote={Object.keys(responses).length >= 2}
        />

        <ActivityResultQuestionProvider
          activity={activity}
          answers={quizAnswer}
          questions={quizQuestion}
          responses={responses}
          students={students}
          questionPosition={questionPosition}
          room={room}
          questionScores={questionScores}
        >
          <ActivityLiveEvent
            isLive={activity.activityStage === ActivityStage.InProgress}
            activityInstanceId={activity.id}
            isRosteredRoom={activity.isRostered}
            pubnub={window.pubnub}
            roomName={room.nameLower}
          />
          <Stack dividers>
            <InviteStudentDrawerContext>
            <ReportAnalyticsProvider
                analyticsData={{
                  activity,
                  studentCount: Object.keys(students).length,
                  questionCount: Object.keys(quizQuestion).length,
                }}
              >
                {teacherApplicationContext === TeacherApplicationContext.ShowbieAssessments ? (
                  <QuizResultsHeaderSansControls
                    title={title}
                    quizStandard={quiz.standard?.name}
                    startDate={activity.startTime}
                    endDate={activity.endTime}
                    activityType={activity.activityType}
                    activitySettings={activity.settings}
                    activityInstanceId={activity.id}
                  />
                ) : isFinishedActivity ? (
                  <QuizResultsHeaderFinished
                    title={title}
                    quizStandard={quiz.standard?.name}
                    startDate={activity.startTime}
                    endDate={activity.endTime}
                    activityType={activity.activityType}
                    activityInstanceId={activity.id}
                    activitySettings={activity.settings}
                  />
                ) : (
                  <QuizResultsHeaderInProgress
                    title={title}
                    quizStandard={quiz.standard?.name}
                    startDate={activity.startTime}
                    activityInstanceId={activity.id}
                    activitySettings={activity.settings}
                    onActivityFinished={handleFinishActivity}
                  />
                )}
              </ReportAnalyticsProvider>
              {shouldShowNavigation ? (
                <ResultsQuestionNavigation
                  onBackToResultsClick={onBackToResultsClick}
                  onPrevClick={onPreviousQuestionClick}
                  onNextClick={onNextQuestionClick}
                  isNavigationDisabled={isUpdating}
                  isNavigationVisible={!isQuickQuestion}
                  isBackToResultsVisible={
                    (!isQuickQuestion &&
                      activity.settings.pacing === ActivityPacing.Student) ||
                    activity.activityStage === ActivityStage.Finished
                  }
                />
              ) : null}
              <div className="grid auto-rows-question-layout grid-cols-question-layout lg:grid-cols-question-layout-wide lg:gap-4 px-0">
                <ResultsQuestionText
                  order={isQuickQuestion ? null : questionPosition}
                />
                {showGradingWeight && (
                  <div className="pl-15">
                    <ResultsQuestionGradingWeight />
                  </div>
                )}

                <ResultsQuestionMedia />
                <QuestionContents>
                  <ResultsQuestionOptions
                    showResults={showResults}
                    setShowResults={setShowResults}
                    showAnswers={showAnswers}
                    setShowAnswers={setShowAnswers}
                    showNames={showNames}
                    setShowNames={setShowNames}
                    onStartVoteClicked={() => {
                      setIsStartVoteModalOpen(true);
                    }}
                  />

                  <div className="max-w-5xl pt-8">
                    <ResultsQuestionAnswers
                      showResults={showResults}
                      showAnswers={showAnswers}
                      showNames={showNames}
                      openErrorModal={openErrorDialog}
                    />
                  </div>

                  {!isQuickQuestion ? (
                    <ResultsQuestionExplanation key={questionPosition} />
                  ) : null}
                </QuestionContents>
              </div>
            </InviteStudentDrawerContext>
          </Stack>
          {isQuickQuestion && (
            <ResultsQuickLauncher
              room={room}
              activityStage={activity.activityStage}
            />
          )}
        </ActivityResultQuestionProvider>
      </div>
    );
  }
  return <ActivityResultLoading />;
}
